/**
 * Cache do ÚLTIMO FRAME de cada vídeo (Renato 2026-07-05) — pra transição
 * vídeo→X sem preto SEM screenshot em runtime a cada troca.
 *
 * Ideia: na 1ª vez que um vídeo toca, capturamos 1× o último frame (no fim, no
 * `handleVideoEnded`) e guardamos aqui, chaveado pelo id do conteúdo. Nas próximas
 * vezes, o Stream desenha esse frame no canvas na troca → cobre o preto até o
 * próximo conteúdo pintar.
 *
 * - 1ª exibição do vídeo: sem frame ainda → transição com preto curto.
 * - 2ª exibição em diante: frame pronto → ZERO preto.
 *
 * ⚠️ DOIS NÍVEIS (2026-10-01). Um HTMLImageElement retido prende o bitmap
 * DECODIFICADO (~8MB em 1080p), não o JPEG (~100-300KB). Por isso:
 *
 * 1. `stored`: o JPEG comprimido (Blob) de cada vídeo — barato, teto alto
 *    (`LAST_FRAME_CACHE_CAP`).
 * 2. `ready`: o frame DECODIFICADO, só do que está pra ser usado — o Stream chama
 *    `prepare(id)` quando o vídeo COMEÇA a tocar, e a decodificação termina muito
 *    antes do fim dele. Teto `LAST_FRAME_READY_CAP`.
 *
 * Antes era um LRU único de 6 bitmaps decodificados. Como o frame só é consumido
 * na volta SEGUINTE do loop, um loop com mais de 6 vídeos distintos tinha 0% de
 * acerto: cada entrada era despejada antes de ser usada → preto em TODA troca
 * (medido no embedded, SM-X520, grupo com 8 vídeos: 3 quadros pretos a cada 10s).
 *
 * Escopo de sessão (em memória): some no restart do app → re-captura na 1ª
 * exibição pós-boot de cada vídeo.
 */

/** Teto de JPEGs guardados. ~32 × ~150KB ≈ 5MB — cobre loops longos com folga. */
export const LAST_FRAME_CACHE_CAP = 32;

/** Teto de frames decodificados (~8MB cada): o vídeo atual + o que acabou de sair. */
export const LAST_FRAME_READY_CAP = 2;

interface ReadyFrame {
  img: HTMLImageElement;
  url: string;
}

const stored = new Map<string, Blob>();
const ready = new Map<string, ReadyFrame>();
/**
 * Ids cujo JPEG é PROVISÓRIO: capturado ~0,4s ANTES do fim, na 1ª exibição, só pra não
 * ficar sem cover na 1ª troca. O frame de fim REAL (capturado no `ended`) substitui —
 * `hasStored` devolve false pra provisório justamente pra essa recaptura acontecer.
 */
const provisional = new Set<string>();

function dropReady(id: string): void {
  const frame = ready.get(id);
  if (!frame) return;
  ready.delete(id);
  URL.revokeObjectURL(frame.url);
}

function touchLru<T>(map: Map<string, T>, id: string, value: T): void {
  map.delete(id);
  map.set(id, value);
}

export const lastFrameCache = {
  /** Frame DECODIFICADO pronto pra desenhar agora (é o que a troca precisa). */
  has(id: string): boolean {
    const frame = ready.get(id);
    return !!frame && frame.img.complete && frame.img.naturalWidth > 0;
  },
  /**
   * JPEG DEFINITIVO já capturado (guard da captura + 🎞️ do painel). Sem toque LRU.
   * Provisório (1ª exibição, antes do fim) conta como "ainda não": o `ended` recaptura.
   */
  hasStored(id: string): boolean {
    return stored.has(id) && !provisional.has(id);
  },
  /** Há algum frame (definitivo ou provisório) pra cobrir uma troca. */
  hasAny(id: string): boolean {
    return stored.has(id);
  },
  get(id: string): HTMLImageElement | null {
    return lastFrameCache.has(id) ? ready.get(id)!.img : null;
  },
  /** Guarda o JPEG do último frame. Uma captura nova invalida o decodificado antigo. */
  store(id: string, jpeg: Blob): void {
    provisional.delete(id);
    touchLru(stored, id, jpeg);
    dropReady(id);
    while (stored.size > LAST_FRAME_CACHE_CAP) {
      const oldestId = stored.keys().next().value as string | undefined;
      if (oldestId === undefined) break;
      stored.delete(oldestId);
      dropReady(oldestId);
    }
  },
  /**
   * Guarda um frame PROVISÓRIO (1ª exibição, capturado pouco antes do fim). Serve de cover
   * na 1ª troca; o `ended` grava o definitivo por cima. Nunca substitui um definitivo.
   */
  storeProvisional(id: string, jpeg: Blob): void {
    if (stored.has(id) && !provisional.has(id)) return; // já tem o frame de fim real
    provisional.add(id);
    touchLru(stored, id, jpeg);
    dropReady(id);
    while (stored.size > LAST_FRAME_CACHE_CAP) {
      const oldestId = stored.keys().next().value as string | undefined;
      if (oldestId === undefined) break;
      stored.delete(oldestId);
      provisional.delete(oldestId);
      dropReady(oldestId);
    }
  },
  /**
   * Decodifica o frame de [id] em segundo plano pra estar pronto na troca. Chamar
   * quando o vídeo COMEÇA a tocar. No-op se não há JPEG ou já está pronto/decodificando.
   */
  prepare(id: string): void {
    const jpeg = stored.get(id);
    if (!jpeg) return;
    const existing = ready.get(id);
    if (existing) {
      touchLru(ready, id, existing);
      return;
    }
    const url = URL.createObjectURL(jpeg);
    const img = new Image();
    img.src = url;
    ready.set(id, { img, url });
    while (ready.size > LAST_FRAME_READY_CAP) {
      const oldestId = ready.keys().next().value as string | undefined;
      if (oldestId === undefined) break;
      dropReady(oldestId);
    }
  },
  delete(id: string): void {
    stored.delete(id);
    provisional.delete(id);
    dropReady(id);
  },
  /**
   * Mantém só os ids informados (a playlist atual). O chamador NÃO deve passar lista
   * vazia por playlist transitoriamente vazia (sync/NoContent) — descartaria tudo.
   */
  retain(ids: Iterable<string>): void {
    const keep = new Set(ids);
    [...stored.keys()].forEach((id) => {
      if (!keep.has(id)) lastFrameCache.delete(id);
    });
    [...ready.keys()].forEach((id) => {
      if (!keep.has(id)) dropReady(id);
    });
  },
  /** Quantos JPEGs guardados. */
  size(): number {
    return stored.size;
  },
  /** Quantos frames decodificados retidos (o que pesa na RAM). */
  readySize(): number {
    return ready.size;
  },
  /** Esvazia tudo. Chamado pelo `cache:clear` do RemoteOps e pelos testes. */
  clear(): void {
    [...ready.keys()].forEach(dropReady);
    stored.clear();
    provisional.clear();
  },
};
